import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import { canonicalFor } from "@/lib/site";

const MATERIALS = [
  {
    name: "Architectural Asphalt Shingles",
    life: "30–50 yr",
    pros: [
      "Most affordable lifecycle cost",
      "Wide color & style range",
      "Class-A fire rated",
    ],
    brands: "GAF Timberline HDZ, IKO Cambridge, Malarkey Vista",
  },
  {
    name: "Standing-Seam Metal",
    life: "50+ yr",
    pros: [
      "Lifetime durability",
      "Modern architectural look",
      "Excellent in snow & rain loads",
    ],
    brands: "Westform, Vicwest, Agway",
  },
  {
    name: "Cedar Shake & Shingle",
    life: "25–40 yr",
    pros: [
      "Premium curb appeal",
      "Natural insulation properties",
      "Heritage-home compatible",
    ],
    brands: "Premium #1 grade, BC-sourced western red cedar",
  },
  {
    name: "Synthetic Composite",
    life: "40+ yr",
    pros: [
      "Slate / cedar appearance",
      "Lightweight, impact resistant",
      "Class 4 hail-rated options",
    ],
    brands: "DaVinci, Brava, F-Wave",
  },
];

const STEPS = [
  { n: "01", title: "On-Site Measurement", desc: "Every plane measured, every penetration documented. We never quote from satellite alone." },
  { n: "02", title: "Material Selection", desc: "Samples brought to your home. Color-match against siding, trim, and neighborhood." },
  { n: "03", title: "Detailed Proposal", desc: "Line-item pricing, material specs, warranty terms, projected timeline." },
  { n: "04", title: "Tear-Off & Installation", desc: "Complete tear-off to deck, new ice & water, synthetic underlayment, ventilation, shingles, flashing — installed in sequence by certified crews." },
  { n: "05", title: "Final Inspection & Cleanup", desc: "Magnetic nail sweep three passes. Independent QA walk-through. You sign off before we leave." },
];

const WARRANTY = [
  { icon: ShieldCheck, title: "Manufacturer System Warranty", desc: "Up to 50 years on materials. Includes algae & wind coverage when installed by a certified contractor — which we are." },
  { icon: FileText, title: "10-Year Workmanship Warranty", desc: "In writing. Transferable to next owner. Covers any installation defect, no fine print." },
];

export default function RoofInstallationPage() {
  return (
    <>
      <Seo
        title="Roof Installation | Crestline Roofing Ltd."
        description="Need a new roof? Crestline Roofing Ltd. installs all roofing types across Vancouver and Fraser Valley. Quality materials, guaranteed workmanship. Free estimates."
        canonical={canonicalFor("/services/roof-installation")}
      />
      <PageLayout>
        {({ openEstimate }) => (
          <main data-testid="roof-installation-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="Roof Installation"
              title="New Roof Installation — Vancouver & Fraser Valley"
              subtitle="Full tear-off and new roof systems for homes across Vancouver, the North Shore, Burnaby, Surrey, Coquitlam, Richmond, Abbotsford, and the rest of the Fraser Valley."
              image="https://images.unsplash.com/photo-1686835162294-c4cd78c7eab6?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Newly installed asphalt shingle roof on a residential home — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[
                { label: "Home", href: "/" },
                { label: "Services", href: "/services" },
                { label: "Roof Installation" },
              ]}
            />

            {/* Materials */}
            <section
              data-testid="install-materials"
              className="relative shingle-texture bg-[var(--crest-slate)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      Materials We Install
                    </span>
                  </div>
                  <h2
                    className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(2rem, 4.8vw, 3.8rem)" }}
                  >
                    Roofing Materials
                    <br />
                    <span className="text-white/50">We Work With.</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {MATERIALS.map((m, i) => (
                    <article
                      key={m.name}
                      data-testid={`install-material-${i}`}
                      className="reveal card-lift bg-[#141d2e] border border-white/10 border-t-2 border-t-[var(--crest-amber)] p-7 md:p-9"
                      style={{ transitionDelay: `${i * 70}ms` }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-display uppercase font-bold text-xl md:text-2xl text-white tracking-tight max-w-[70%]">
                          {m.name}
                        </h3>
                        <span className="font-display uppercase tracking-[0.25em] text-[10px] font-semibold text-[var(--crest-amber)] px-2.5 py-1 border border-[var(--crest-amber)]/40 whitespace-nowrap">
                          {m.life}
                        </span>
                      </div>
                      <ul className="space-y-2 mb-5">
                        {m.pros.map((p) => (
                          <li
                            key={p}
                            className="font-body text-sm text-white/70 flex items-center gap-3"
                          >
                            <span className="h-px w-4 bg-[var(--crest-amber)]" />
                            {p}
                          </li>
                        ))}
                      </ul>
                      <p className="font-body text-xs uppercase tracking-[0.2em] text-white/40 pt-4 border-t border-white/10">
                        {m.brands}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            {/* Process */}
            <section
              data-testid="install-process"
              className="relative bg-[var(--crest-charcoal)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-16">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      Installation Process
                    </span>
                  </div>
                  <h2
                    className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(2rem, 4.8vw, 3.8rem)" }}
                  >
                    Our Installation Process.
                  </h2>
                </div>

                <div className="space-y-1">
                  {STEPS.map((s, i) => (
                    <div
                      key={s.n}
                      data-testid={`install-step-${i + 1}`}
                      className="reveal grid grid-cols-12 gap-6 py-8 border-t border-white/10 last:border-b last:border-white/10"
                      style={{ transitionDelay: `${i * 80}ms` }}
                    >
                      <div className="col-span-12 md:col-span-2 font-display font-extrabold text-3xl md:text-4xl text-[var(--crest-amber)] leading-none">
                        {s.n}
                      </div>
                      <h3 className="col-span-12 md:col-span-3 font-display uppercase font-bold text-xl text-white tracking-tight">
                        {s.title}
                      </h3>
                      <p className="col-span-12 md:col-span-7 font-body text-sm md:text-base text-white/65 leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Warranty */}
            <section
              data-testid="install-warranty"
              className="relative bg-[var(--crest-cream)] text-[#121212] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal max-w-3xl mb-14">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      Backed in Writing
                    </span>
                  </div>
                  <h3
                    className="font-display uppercase font-bold leading-[0.95] tracking-tight text-[#121212]"
                    style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}
                  >
                    Two warranties. Zero loopholes.
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {WARRANTY.map((w, i) => {
                    const Icon = w.icon;
                    return (
                      <div
                        key={w.title}
                        data-testid={`warranty-${i}`}
                        className="reveal flex gap-5"
                        style={{ transitionDelay: `${i * 100}ms` }}
                      >
                        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#121212] text-[var(--crest-amber)]">
                          <Icon size={22} strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="font-display uppercase font-bold text-xl tracking-tight mb-2">
                            {w.title}
                          </h3>
                          <p className="font-body text-sm md:text-base text-[#3a3a3a] leading-relaxed">
                            {w.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="reveal mt-14">
                  <Link
                    to="/services/roof-repair"
                    data-testid="link-to-repair"
                    className="group inline-flex items-center gap-2 font-display uppercase tracking-[0.3em] text-xs font-semibold text-[#121212] hover:text-[var(--crest-amber)] border-b border-[#121212]/30 hover:border-[var(--crest-amber)] pb-1.5 transition-colors"
                  >
                    Just need a repair instead?
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </section>

            <CTABanner
              onOpenEstimate={openEstimate}
              eyebrow="Ready to replace your roof?"
              heading="Get Your Free Roofing Estimate."
              subheading="Site visit, materials samples, line-item quote in 48 hours."
              buttonLabel="Request My Estimate"
            />
          </main>
        )}
      </PageLayout>
    </>
  );
}
